<template>
  <EquipmentRow
    ref="equipmentRow"
    :key="`tubing-${tubing?.id}`"
    data-cy="tubing-row-equipment-row"
  >
    <template #left></template>
    <template #default="{ toggle }">
      <AddRemoveButtons
        :disable-add-above="disableAddAbove"
        :disable-add-below="disableAddBelow"
        :disable-remove="disableRemove"
        :hide-add-above="hideAddAbove"
        :hide-add-below="hideAddBelow"
        :hide-remove="hideRemove"
        data-cy="tubing-row-add-remove-buttons"
        @add-below="$emit('add-below')"
        @remove="$emit('remove')"
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            :rule-names="
              topTubing
                ? [
                    'tubing_length_check',
                    'top_tubing_size_top_casing',
                    'top_tubing_size_bottom_casing',
                    'pump_setting_depth',
                    'top_tubing_obsolete',
                    'tubing_length_below_threshold'
                  ]
                : [
                    'tubing_length_check',
                    'bottom_tubing_size_top_casing',
                    'bottom_tubing_size_bottom_casing',
                    'pump_setting_depth',
                    'bottom_tubing_obsolete'
                  ]
            "
            data-cy="tubing-row-equipment-panel"
            @toggle="toggle"
          >
            <template #header>
              <span data-cy="tubing-row-panel-header">
                {{ tubingRowLabel }}
              </span>
            </template>
            <template v-if="tubing" #trigger>
              <span data-cy="tubing-row-panel-trigger">
                {{
                  computeTubingLabel(
                    tubing,
                    getLargeLengthUnit(`${tubingLengthLabel}.write.Feet`).value
                      .label === LengthUnits.Meters.label
                  )
                }}
              </span>
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <TubingSearchView
        :is-admin="false"
        :allowed-tubing-types="allowedTubingTypes"
        data-cy="tubing-row-search-view"
        @select="handleTubingSelect($event, toggle)"
      />
    </template>
    <template #right>
      <div class="flex">
        <ConverterInput
          data-cy="tubing-row-converter-input"
          :label="tubingLengthLabel"
          :model-value="
            topTubing
              ? wellProperties?.top_tubing_length_feet
              : wellProperties?.bottom_tubing_length_feet
          "
          :disabled="
            topTubing
              ? !wellProperties?.top_tubing_id
              : !wellProperties?.bottom_tubing_id
          "
          :base-unit="LengthUnits.Feet"
          :model="LargeLengthUnits.options()"
          mode="write"
          @update:model-value="
            wellPropertiesForm.update({
              [tubingLengthKey]: $event
            })
          "
        />
        <ConverterInput
          label="Weight"
          :model-value="tubingWeightPounds"
          :base-unit="MassUnits.Pounds"
          :model="MassUnits.options()"
          mode="convert"
        />
      </div>
    </template>
  </EquipmentRow>
</template>
<script setup lang="ts">
import type { PropType } from "vue";
import { computed, provide } from "vue";
import type { Tubings, Tubings_Type_Enum } from "gql/api";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { computeTubingLabel } from "@/models/equipment/tubings/properties";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import TubingSearchView from "./TubingSearchView.vue";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import {
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";

const props = defineProps({
  tubing: {
    type: Object as PropType<Tubings>,
    default: null
  },
  topTubing: {
    type: Boolean,
    default: true
  },
  allowedTubingTypes: {
    type: Array as PropType<Tubings_Type_Enum[]>,
    default: undefined // undefined means all tubing types are allowed (the default behavior)
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  }
});

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "remove"): void;
}>();

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellProperties = computed(() => wellPropertiesForm.data.value);
const scenarioCalculations = injectStrict(scenarioCalculationsKey);

const tubingIdKey = computed(() =>
  props.topTubing ? "top_tubing_id" : "bottom_tubing_id"
);
const tubingRelationKey = computed(() =>
  props.topTubing ? "r_top_tubing" : "r_bottom_tubing"
);
const tubingLengthKey = computed(() =>
  props.topTubing ? "top_tubing_length_feet" : "bottom_tubing_length_feet"
);
const tubingRowLabel = computed(() =>
  props.topTubing ? "Top Tubing" : "Bottom Tubing"
);
const tubingLengthLabel = computed(() =>
  props.topTubing ? "Top Tubing Length" : "Bottom Tubing Length"
);
const tubingWeightPounds = computed(() =>
  props.topTubing
    ? scenarioCalculations.equipmentWeights.topTubingWeightLbs.value
    : scenarioCalculations.equipmentWeights.bottomTubingWeightLbs.value
);

const handleTubingSelect = (tubing: Tubings, toggle: () => void) => {
  toggle();
  wellPropertiesForm.update({
    [tubingRelationKey.value]: tubing,
    [tubingIdKey.value]: tubing.id
  });
};

provide("equipment", undefined);
provide(
  "selected-equipment-id",
  computed(() => props.tubing?.id)
);
</script>
